// A minimal read-only SQLite reader: enough to walk every row of a table.
//
// MacFamilyTree keeps its data in a Core Data SQLite store. Rather than ship a 1 MB
// WebAssembly build of SQLite to read one file format, this reads the on-disk format
// directly (https://www.sqlite.org/fileformat2.html): the header, table b-trees,
// records, and overflow pages. It doesn't use indexes or run SQL; callers scan tables.
// Committed pages from a write-ahead log (the `-wal` file next to the database) are
// applied first, so a store whose app didn't checkpoint still reads correctly.

export type SqlValue = null | number | string | Uint8Array;
export type SqlRow = Record<string, SqlValue>;

const MAGIC = 'SQLite format 3\0';

export function looksLikeSqlite(bytes: Uint8Array): boolean {
  if (bytes.length < 100) return false;
  for (let i = 0; i < MAGIC.length; i++) if (bytes[i] !== MAGIC.charCodeAt(i)) return false;
  return true;
}

interface TableInfo {
  root: number;
  columns: string[];
  /** Index of an INTEGER PRIMARY KEY column, which is stored as the rowid instead of in the record. */
  rowidColumn: number;
}

export class SqliteDb {
  private bytes: Uint8Array;
  private view: DataView;
  readonly pageSize: number;
  private usable: number;
  private decoder: TextDecoder;
  /** Page number → replacement page from the write-ahead log. */
  private walPages = new Map<number, Uint8Array>();
  private tables = new Map<string, TableInfo>();

  constructor(bytes: Uint8Array, wal?: Uint8Array) {
    if (!looksLikeSqlite(bytes)) throw new Error("This isn't an SQLite database.");
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const ps = this.view.getUint16(16);
    this.pageSize = ps === 1 ? 65536 : ps;
    this.usable = this.pageSize - bytes[20];
    const enc = this.view.getUint32(56);
    this.decoder = new TextDecoder(enc === 2 ? 'utf-16le' : enc === 3 ? 'utf-16be' : 'utf-8');
    if (wal) this.readWal(wal);
    this.readSchema();
  }

  tableNames(): string[] {
    return [...this.tables.keys()];
  }

  hasTable(name: string): boolean {
    return this.tables.has(name.toUpperCase());
  }

  columns(table: string): string[] {
    return this.tables.get(table.toUpperCase())?.columns ?? [];
  }

  /** Every row of a table, as column → value. Missing tables give no rows. */
  rows(table: string): SqlRow[] {
    const info = this.tables.get(table.toUpperCase());
    if (!info) return [];
    const out: SqlRow[] = [];
    this.walk(info.root, (rowid, values) => {
      const row: SqlRow = {};
      info.columns.forEach((c, i) => {
        // Columns added after a row was written aren't in its record; they read as NULL.
        row[c] = i === info.rowidColumn ? rowid : (values[i] ?? null);
      });
      out.push(row);
    });
    return out;
  }

  // ---- Pages

  private page(n: number): Uint8Array {
    const fromWal = this.walPages.get(n);
    if (fromWal) return fromWal;
    const start = (n - 1) * this.pageSize;
    if (n < 1 || start + this.pageSize > this.bytes.length) throw new Error(`The database is damaged (page ${n} is missing).`);
    return this.bytes.subarray(start, start + this.pageSize);
  }

  private readWal(wal: Uint8Array) {
    if (wal.length < 32) return;
    const v = new DataView(wal.buffer, wal.byteOffset, wal.byteLength);
    const magic = v.getUint32(0);
    if (magic !== 0x377f0682 && magic !== 0x377f0683) return;
    const pageSize = v.getUint32(8);
    if (pageSize !== this.pageSize) return;
    const salt1 = v.getUint32(16);
    const salt2 = v.getUint32(20);
    const frameSize = 24 + pageSize;
    // Only frames up to the last commit count; later ones belong to an unfinished transaction.
    const pending = new Map<number, Uint8Array>();
    for (let off = 32; off + frameSize <= wal.length; off += frameSize) {
      if (v.getUint32(off + 8) !== salt1 || v.getUint32(off + 12) !== salt2) break;
      const pageNo = v.getUint32(off);
      pending.set(pageNo, wal.subarray(off + 24, off + frameSize));
      if (v.getUint32(off + 4) !== 0) {
        for (const [k, p] of pending) this.walPages.set(k, p);
        pending.clear();
      }
    }
  }

  // ---- Schema

  private readSchema() {
    this.walk(1, (_rowid, values) => {
      const [type, name, , root, sql] = values;
      if (type !== 'table' || typeof name !== 'string' || typeof root !== 'number' || typeof sql !== 'string') return;
      const { columns, rowidColumn } = parseCreateTable(sql);
      this.tables.set(name.toUpperCase(), { root, columns, rowidColumn });
    });
  }

  // ---- B-trees and records

  private walk(root: number, visit: (rowid: number, values: SqlValue[]) => void) {
    const stack = [root];
    const seen = new Set<number>();
    while (stack.length) {
      const n = stack.pop()!;
      if (seen.has(n)) throw new Error('The database is damaged (a page loops back on itself).');
      seen.add(n);
      const page = this.page(n);
      const hdr = n === 1 ? 100 : 0;
      const pv = new DataView(page.buffer, page.byteOffset, page.byteLength);
      const kind = page[hdr];
      const cellCount = pv.getUint16(hdr + 3);
      if (kind === 0x05) {
        // Interior table page: visit children right to left so rows come out in rowid order.
        stack.push(pv.getUint32(hdr + 8));
        for (let i = cellCount - 1; i >= 0; i--) stack.push(pv.getUint32(pv.getUint16(hdr + 12 + i * 2)));
      } else if (kind === 0x0d) {
        for (let i = 0; i < cellCount; i++) {
          let off = pv.getUint16(hdr + 8 + i * 2);
          const [payloadSize, a] = varint(page, off);
          off += a;
          const [rowid, b] = varint(page, off);
          off += b;
          visit(rowid, this.record(this.payload(page, off, payloadSize)));
        }
      } else if (kind !== 0x02 && kind !== 0x0a) {
        throw new Error(`The database is damaged (page ${n} has an unknown type).`);
      }
    }
  }

  /** A cell's payload, following overflow pages when it doesn't fit on its page. */
  private payload(page: Uint8Array, off: number, size: number): Uint8Array {
    const u = this.usable;
    const x = u - 35;
    if (size <= x) return page.subarray(off, off + size);
    const m = Math.floor(((u - 12) * 32) / 255) - 23;
    const k = m + ((size - m) % (u - 4));
    const local = k <= x ? k : m;
    const out = new Uint8Array(size);
    out.set(page.subarray(off, off + local), 0);
    let filled = local;
    let next = new DataView(page.buffer, page.byteOffset).getUint32(off + local);
    while (filled < size && next) {
      const ov = this.page(next);
      const take = Math.min(u - 4, size - filled);
      out.set(ov.subarray(4, 4 + take), filled);
      filled += take;
      next = new DataView(ov.buffer, ov.byteOffset).getUint32(0);
    }
    return out;
  }

  private record(p: Uint8Array): SqlValue[] {
    const [headerSize, n0] = varint(p, 0);
    const types: number[] = [];
    let h = n0;
    while (h < headerSize) {
      const [t, n] = varint(p, h);
      types.push(t);
      h += n;
    }
    const v = new DataView(p.buffer, p.byteOffset, p.byteLength);
    const out: SqlValue[] = [];
    let off = headerSize;
    for (const t of types) {
      if (t === 0) out.push(null);
      else if (t >= 1 && t <= 6) {
        const len = [0, 1, 2, 3, 4, 6, 8][t];
        out.push(readInt(p, off, len));
        off += len;
      } else if (t === 7) {
        out.push(v.getFloat64(off));
        off += 8;
      } else if (t === 8 || t === 9) out.push(t - 8);
      else if (t >= 12) {
        const len = t % 2 === 0 ? (t - 12) / 2 : (t - 13) / 2;
        const bytes = p.subarray(off, off + len);
        out.push(t % 2 === 0 ? bytes.slice() : this.decoder.decode(bytes));
        off += len;
      } else out.push(null);
    }
    return out;
  }
}

function varint(b: Uint8Array, off: number): [number, number] {
  let v = 0;
  for (let i = 0; i < 8; i++) {
    const byte = b[off + i];
    v = v * 128 + (byte & 0x7f);
    if (!(byte & 0x80)) return [v, i + 1];
  }
  return [v * 256 + b[off + 8], 9];
}

/** Big-endian two's-complement integer. Values past 2^53 lose precision, which is fine for rowids and flags. */
function readInt(b: Uint8Array, off: number, len: number): number {
  let v = b[off] & 0x80 ? -1 : 0;
  for (let i = 0; i < len; i++) v = v * 256 + b[off + i];
  return v;
}

/** Column names from a CREATE TABLE statement, and which one (if any) is the rowid alias. */
export function parseCreateTable(sql: string): { columns: string[]; rowidColumn: number } {
  const open = sql.indexOf('(');
  const close = sql.lastIndexOf(')');
  if (open < 0 || close < open) return { columns: [], rowidColumn: -1 };
  const body = sql.slice(open + 1, close);
  const defs: string[] = [];
  let depth = 0;
  let quote = '';
  let cur = '';
  for (const ch of body) {
    if (quote) {
      if (ch === quote) quote = '';
    } else if (ch === '"' || ch === "'" || ch === '`' || ch === '[') quote = ch === '[' ? ']' : ch;
    else if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ',' && depth === 0) {
      defs.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  defs.push(cur);
  const columns: string[] = [];
  let rowidColumn = -1;
  for (const d of defs) {
    const t = d.trim();
    if (!t || /^(CONSTRAINT|PRIMARY|UNIQUE|CHECK|FOREIGN)\b/i.test(t)) continue;
    const m = t.match(/^(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\S+))\s*(.*)$/s);
    if (!m) continue;
    const name = (m[1] ?? m[2] ?? m[3] ?? m[4]).toUpperCase();
    if (/^INTEGER\s+PRIMARY\s+KEY\b/i.test(m[5])) rowidColumn = columns.length;
    columns.push(name);
  }
  return { columns, rowidColumn };
}
